// src/components/PropertiesPanel.tsx
// Compact properties panel replacing the stacked Card layout in task detail.
// Pure view component — all data and callbacks come from props.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { cn } from '@core/ui';
import {
  formatDate,
  formatRelativeTime,
  formatDuration,
  getDueDateUrgency,
  getAvatarColor,
  getInitials,
  getTaskTypeInfo,
  taskPriorityMap,
  getStatusMapping,
} from '@/features/task/logic';
import { PropertyField } from '@/components/PropertyField';
import { StatusSelect } from '@/components/StatusSelect';
import { PrioritySelect } from '@/components/PrioritySelect';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PropertiesPanelProps {
  // Core properties
  status?: string;
  priority?: string;
  assignee?: { id: string; display_name: string } | null;
  dueDate?: string;
  sprint?: string;
  type?: string;
  labels?: string[];
  // Progress
  progress?: number;
  timeTracked?: string;
  timeEstimated?: string;
  // Metadata
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  // Context
  scopeLabel?: string;
  projectName?: string;
  projectId?: string;
  parentKey?: string;
  parentTitle?: string;
  // Watchers
  watchers?: { id: string; display_name: string }[];
  isWatching?: boolean;
  // Callbacks (for inline editing)
  onStatusChange?: (status: string) => void;
  onPriorityChange?: (priority: string) => void;
  onAssigneeChange?: (assigneeId: string) => void;
  onDueDateChange?: (date: string) => void;
  onSprintChange?: (sprint: string) => void;
  onTypeChange?: (type: string) => void;
  onLabelsChange?: (labels: string[]) => void;
  onToggleWatch?: () => void;
  onLinkResource?: () => void;
  // Optional class
  className?: string;
}

// ---------------------------------------------------------------------------
// Section divider
// ---------------------------------------------------------------------------

function Divider() {
  return <div className="border-t border-border my-2" />;
}

// ---------------------------------------------------------------------------
// Section heading
// ---------------------------------------------------------------------------

function SectionHeading({ labelKey }: { labelKey: string }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2 pt-1 pb-0.5">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {t(labelKey)}
      </span>
      <div className="flex-1 border-t border-border" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small avatar circle
// ---------------------------------------------------------------------------

function AvatarCircle({ name, size = 'sm' }: { name: string; size?: 'sm' | 'xs' }) {
  const bg = getAvatarColor(name);
  const initials = getInitials(name);
  const sizeClass = size === 'sm' ? 'h-6 w-6 text-xs' : 'h-5 w-5 text-[10px]';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full font-medium text-white shrink-0',
        sizeClass,
      )}
      style={{ backgroundColor: bg }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PropertiesPanel = observer(function PropertiesPanel(props: PropertiesPanelProps) {
  const {
    status,
    priority,
    assignee,
    dueDate,
    sprint,
    type,
    labels,
    progress,
    timeTracked,
    timeEstimated,
    createdAt,
    createdBy,
    updatedAt,
    scopeLabel,
    projectName,
    parentKey,
    parentTitle,
    watchers,
    isWatching,
    onStatusChange,
    onPriorityChange,
    onAssigneeChange,
    onDueDateChange,
    onSprintChange,
    onTypeChange,
    onLabelsChange,
    onToggleWatch,
    onLinkResource,
    className,
  } = props;

  const { t } = useI18n();

  const dueDateUrgency = getDueDateUrgency(dueDate, status || '');
  const typeInfo = getTaskTypeInfo(type);
  const clampedProgress = Math.min(100, Math.max(0, progress ?? 0));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className={cn('space-y-0.5', className)} role="region" aria-label={t('task.properties')}>
      {/* ── Main Properties ─────────────────────────────────── */}

      {/* Status */}
      <PropertyField label="task.field.status">
        {onStatusChange && status != null ? (
          <StatusSelect
            value={status}
            onSelect={onStatusChange}
          />
        ) : (
          <span className="text-muted-foreground">{status ? t(`task.status.${status}`) : '--'}</span>
        )}
      </PropertyField>

      {/* Priority */}
      <PropertyField label="task.field.priority">
        {onPriorityChange && priority != null ? (
          <PrioritySelect
            value={priority}
            onSelect={onPriorityChange}
          />
        ) : (
          <span className="text-muted-foreground">
            {priority ? t(getStatusMapping(taskPriorityMap, priority).labelKey) : '--'}
          </span>
        )}
      </PropertyField>

      {/* Assignee */}
      <PropertyField
        label="task.field.assignee"
        editable={!!onAssigneeChange}
        onClick={onAssigneeChange ? () => onAssigneeChange(assignee?.id || '') : undefined}
      >
        {assignee ? (
          <span className="inline-flex items-center gap-1.5">
            <AvatarCircle name={assignee.display_name} size="xs" />
            <span>{assignee.display_name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{t('task.unassigned')}</span>
        )}
      </PropertyField>

      {/* Due Date */}
      <PropertyField
        label="task.field.dueDate"
        editable={!!onDueDateChange}
        onClick={onDueDateChange ? () => onDueDateChange(dueDate || '') : undefined}
      >
        <span
          className={cn(
            dueDateUrgency === 'overdue' && 'text-destructive font-medium',
            dueDateUrgency === 'soon' && 'text-warning font-medium',
          )}
        >
          {dueDate ? formatDate(dueDate) : '--'}
        </span>
        {dueDateUrgency === 'overdue' && (
          <Badge variant="error" size="sm" className="ml-1.5">
            {t('task.overdue')}
          </Badge>
        )}
        {dueDateUrgency === 'soon' && (
          <Badge variant="warning" size="sm" className="ml-1.5">
            {t('task.dueSoon')}
          </Badge>
        )}
      </PropertyField>

      {/* Sprint */}
      {sprint !== undefined && (
        <PropertyField
          label="task.field.sprint"
          editable={!!onSprintChange}
          onClick={onSprintChange ? () => onSprintChange(sprint || '') : undefined}
        >
          {sprint || '--'}
        </PropertyField>
      )}

      {/* Type */}
      <PropertyField
        label="task.field.type"
        editable={!!onTypeChange}
        onClick={onTypeChange ? () => onTypeChange(type || '') : undefined}
      >
        <span className="inline-flex items-center gap-1.5">
          {typeInfo && (
            <svg
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={typeInfo.iconPath} />
            </svg>
          )}
          <span>{typeInfo ? t(typeInfo.labelKey) : (type || '--')}</span>
        </span>
      </PropertyField>

      {/* Labels */}
      <PropertyField
        label="task.labels"
        editable={!!onLabelsChange}
        onClick={onLabelsChange ? () => onLabelsChange(labels || []) : undefined}
      >
        {labels && labels.length > 0 ? (
          <span className="inline-flex flex-wrap items-center gap-1">
            {labels.map((label) => (
              <Badge key={label} variant="secondary" size="sm">
                {label}
              </Badge>
            ))}
          </span>
        ) : (
          <span className="text-muted-foreground">--</span>
        )}
      </PropertyField>

      {/* ── Progress / Time ─────────────────────────────────── */}
      <Divider />

      {/* Progress bar */}
      <PropertyField label="task.progress">
        <span className="inline-flex items-center gap-2 w-full">
          <span className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <span
              className="block h-full bg-primary rounded-full transition-all"
              style={{ width: `${clampedProgress}%` }}
            />
          </span>
          <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">
            {clampedProgress}%
          </span>
        </span>
      </PropertyField>

      {/* Time tracking */}
      {(timeTracked || timeEstimated) && (
        <PropertyField label="task.time">
          <span className="text-foreground tabular-nums">
            {timeTracked ? formatDuration(timeTracked) : '--'}
            {timeEstimated && (
              <span className="text-muted-foreground">
                {' / '}
                {formatDuration(timeEstimated)}
                {' '}{t('task.estimated')}
              </span>
            )}
          </span>
        </PropertyField>
      )}

      {/* Created */}
      {createdAt && (
        <PropertyField label="common.createdAt">
          <span className="text-muted-foreground">
            {formatDate(createdAt)}
            {createdBy && (
              <>
                {' '}{t('common.by')}{' '}
                <span className="text-foreground">{createdBy}</span>
              </>
            )}
          </span>
        </PropertyField>
      )}

      {/* Updated */}
      {updatedAt && (
        <PropertyField label="common.updatedAt">
          <span className="text-muted-foreground">
            {formatRelativeTime(updatedAt)}
          </span>
        </PropertyField>
      )}

      {/* ── Context ─────────────────────────────────────────── */}
      {(scopeLabel || projectName || parentKey) && (
        <>
          <Divider />
          <SectionHeading labelKey="task.context" />

          {scopeLabel && (
            <PropertyField
              label="task.scope"
            >
              {scopeLabel}
            </PropertyField>
          )}

          {projectName && (
            <PropertyField
              label="task.project"
            >
              {projectName}
            </PropertyField>
          )}

          {parentKey && (
            <PropertyField label="task.parent">
              <span className="font-mono text-xs">
                {parentKey}
                {parentTitle && (
                  <span className="text-muted-foreground ml-1">({parentTitle})</span>
                )}
              </span>
            </PropertyField>
          )}
        </>
      )}

      {/* ── Watchers ────────────────────────────────────────── */}
      {(watchers && watchers.length > 0) || onToggleWatch ? (
        <>
          <Divider />
          <SectionHeading labelKey="task.watchers" />

          {watchers && watchers.length > 0 && (
            <div className="flex items-center gap-1 py-1 flex-wrap">
              {watchers.map((watcher) => (
                <span
                  key={watcher.id}
                  className="inline-flex items-center gap-1 text-sm"
                  title={watcher.display_name}
                >
                  <AvatarCircle name={watcher.display_name} size="xs" />
                  <span className="text-foreground">{watcher.display_name}</span>
                </span>
              ))}
            </div>
          )}

          {onToggleWatch && (
            <button
              type="button"
              onClick={onToggleWatch}
              className={cn(
                'inline-flex items-center gap-1 text-sm rounded px-1.5 py-0.5',
                'hover:bg-accent transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isWatching ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-label={isWatching ? t('task.unwatch') : t('task.watch')}
              aria-pressed={isWatching}
            >
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill={isWatching ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>{isWatching ? t('task.watching') : t('task.watch')}</span>
            </button>
          )}
        </>
      ) : null}

      {/* ── Linked Resources ────────────────────────────────── */}
      {onLinkResource && (
        <>
          <Divider />
          <SectionHeading labelKey="task.linkedResources" />

          <button
            type="button"
            onClick={onLinkResource}
            className={cn(
              'inline-flex items-center gap-1.5 text-sm text-muted-foreground rounded px-1.5 py-0.5',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            aria-label={t('task.linkResource')}
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>{t('task.linkResource')}</span>
          </button>
        </>
      )}
    </div>
  );
});

export default PropertiesPanel;
